import { apiClient } from '../instance';
import type {
  HelpdeskSettings,
  RoutingRule,
  WorkShift,
  SLAPolicy,
  OperatorScheduleEntry,
  EmailTestResponse,
  TicketType,
  TicketTopic,
  HDProduct,
  TicketStatusDef,
  ScheduleException,
  HDGroup,
  Branch,
  Mailbox,
  EmailRejectLogEntry,
} from '../../types/helpdeskSettingsInterfaces';

export const helpdeskSettingsApi = {
  // General settings (singleton)
  getSettings: () =>
    apiClient.get<HelpdeskSettings[]>('/helpdesk/settings/general/'),
  updateSettings: (data: Partial<HelpdeskSettings>) =>
    apiClient.patch<HelpdeskSettings>('/helpdesk/settings/general/1/', data),

  // Routing rules
  getRoutingRules: () =>
    apiClient.get<RoutingRule[]>('/helpdesk/settings/routing-rules/'),
  createRoutingRule: (data: Partial<RoutingRule>) =>
    apiClient.post<RoutingRule>('/helpdesk/settings/routing-rules/', data),
  updateRoutingRule: (id: number, data: Partial<RoutingRule>) =>
    apiClient.patch<RoutingRule>(`/helpdesk/settings/routing-rules/${id}/`, data),
  deleteRoutingRule: (id: number) =>
    apiClient.delete(`/helpdesk/settings/routing-rules/${id}/`),
  reorderRoutingRules: (order: number[]) =>
    apiClient.post('/helpdesk/settings/routing-rules/reorder/', { order }),

  // Work shifts (рабочие смены)
  getWorkShifts: () =>
    apiClient.get<WorkShift[]>('/helpdesk/settings/work-shifts/'),
  createWorkShift: (data: Partial<WorkShift>) =>
    apiClient.post<WorkShift>('/helpdesk/settings/work-shifts/', data),
  updateWorkShift: (id: number, data: Partial<WorkShift>) =>
    apiClient.patch<WorkShift>(`/helpdesk/settings/work-shifts/${id}/`, data),
  deleteWorkShift: (id: number) =>
    apiClient.delete(`/helpdesk/settings/work-shifts/${id}/`),
  /** Ручной порядок списка смен (drag&drop) — полный новый порядок id. */
  reorderWorkShifts: (ids: number[]) =>
    apiClient.post<WorkShift[]>('/helpdesk/settings/work-shifts/reorder/', { ids }),
  /** Удалить несколько смен разом. */
  bulkDeleteWorkShifts: (ids: number[]) =>
    apiClient.post<{ deleted: number }>('/helpdesk/settings/work-shifts/bulk-delete/', { ids }),

  // Ticket types (справочник типов заявок)
  getTicketTypes: (activeOnly = false) =>
    apiClient.get<TicketType[]>('/helpdesk/ticket-types/', {
      params: activeOnly ? { active: 1 } : undefined,
    }),
  createTicketType: (data: Partial<TicketType>) =>
    apiClient.post<TicketType>('/helpdesk/ticket-types/', data),
  updateTicketType: (id: number, data: Partial<TicketType>) =>
    apiClient.patch<TicketType>(`/helpdesk/ticket-types/${id}/`, data),
  deleteTicketType: (id: number) =>
    apiClient.delete(`/helpdesk/ticket-types/${id}/`),

  // Ticket topics (справочник «Тип проблемы»)
  getTicketTopics: (activeOnly = false) =>
    apiClient.get<TicketTopic[]>('/helpdesk/ticket-topics/', {
      params: activeOnly ? { active: 1 } : undefined,
    }),
  createTicketTopic: (data: Partial<TicketTopic>) =>
    apiClient.post<TicketTopic>('/helpdesk/ticket-topics/', data),
  updateTicketTopic: (id: number, data: Partial<TicketTopic>) =>
    apiClient.patch<TicketTopic>(`/helpdesk/ticket-topics/${id}/`, data),
  deleteTicketTopic: (id: number) =>
    apiClient.delete(`/helpdesk/ticket-topics/${id}/`),

  // Products (карточки каталога для поля «Продукт» заявки)
  getProducts: (search?: string) =>
    apiClient.get<HDProduct[]>('/helpdesk/products/', {
      params: search ? { search } : undefined,
    }),

  // Ticket statuses (справочник статусов заявок)
  getTicketStatuses: () =>
    apiClient.get<TicketStatusDef[]>('/helpdesk/ticket-statuses/'),
  createTicketStatus: (data: Partial<TicketStatusDef>) =>
    apiClient.post<TicketStatusDef>('/helpdesk/ticket-statuses/', data),
  updateTicketStatus: (id: number, data: Partial<TicketStatusDef>) =>
    apiClient.patch<TicketStatusDef>(`/helpdesk/ticket-statuses/${id}/`, data),
  deleteTicketStatus: (id: number) =>
    apiClient.delete(`/helpdesk/ticket-statuses/${id}/`),

  // SLA policies
  getSLAPolicies: () =>
    apiClient.get<SLAPolicy[]>('/helpdesk/settings/sla-policies/'),
  createSLAPolicy: (data: Partial<SLAPolicy>) =>
    apiClient.post<SLAPolicy>('/helpdesk/settings/sla-policies/', data),
  updateSLAPolicy: (id: number, data: Partial<SLAPolicy>) =>
    apiClient.patch<SLAPolicy>(`/helpdesk/settings/sla-policies/${id}/`, data),
  deleteSLAPolicy: (id: number) =>
    apiClient.delete(`/helpdesk/settings/sla-policies/${id}/`),

  // Operator schedules
  getSchedules: (operatorId?: number) =>
    apiClient.get<OperatorScheduleEntry[]>('/helpdesk/settings/schedules/', {
      params: operatorId ? { operator: operatorId } : undefined,
    }),
  createSchedule: (data: Partial<OperatorScheduleEntry>) =>
    apiClient.post<OperatorScheduleEntry>('/helpdesk/settings/schedules/', data),
  updateSchedule: (id: number, data: Partial<OperatorScheduleEntry>) =>
    apiClient.patch<OperatorScheduleEntry>(`/helpdesk/settings/schedules/${id}/`, data),
  deleteSchedule: (id: number) =>
    apiClient.delete(`/helpdesk/settings/schedules/${id}/`),

  // Schedule exceptions (исключения по датам: оператор или группа)
  getScheduleExceptions: (params?: { date_from?: string; date_to?: string }) =>
    apiClient.get<ScheduleException[]>('/helpdesk/settings/schedule-exceptions/', {
      params,
    }),
  createScheduleException: (data: Partial<ScheduleException>) =>
    apiClient.post<ScheduleException>('/helpdesk/settings/schedule-exceptions/', data),
  deleteScheduleException: (id: number) =>
    apiClient.delete(`/helpdesk/settings/schedule-exceptions/${id}/`),
  getAvailableGroups: () =>
    apiClient.get<HDGroup[]>(
      '/helpdesk/settings/schedule-exceptions/available-groups/',
    ),

  // Available operators for schedules
  getAvailableOperators: () =>
    apiClient.get<{ id: number; username: string; first_name: string; last_name: string; full_name: string }[]>(
      '/helpdesk/settings/schedules/available-operators/',
    ),

  // Email channel test
  testEmailConnection: (testType: 'imap' | 'smtp' | 'both' = 'both') =>
    apiClient.post<EmailTestResponse>('/helpdesk/settings/email-channel/', {
      test_type: testType,
    }),

  // Филиалы поддержки
  getBranches: () => apiClient.get<Branch[]>('/helpdesk/settings/branches/'),
  getEmailRejects: (params?: { mailbox?: number; reason?: string; q?: string; limit?: number }) =>
    apiClient.get<EmailRejectLogEntry[] | { results: EmailRejectLogEntry[] }>(
      '/helpdesk/settings/email-rejects/',
      { params },
    ),
  createBranch: (data: Partial<Branch>) =>
    apiClient.post<Branch>('/helpdesk/settings/branches/', data),
  updateBranch: (id: number, data: Partial<Branch>) =>
    apiClient.patch<Branch>(`/helpdesk/settings/branches/${id}/`, data),
  deleteBranch: (id: number) => apiClient.delete(`/helpdesk/settings/branches/${id}/`),

  // Почтовые ящики поддержки (по филиалам)
  getMailboxes: () => apiClient.get<Mailbox[]>('/helpdesk/settings/mailboxes/'),
  createMailbox: (data: Partial<Mailbox>) =>
    apiClient.post<Mailbox>('/helpdesk/settings/mailboxes/', data),
  updateMailbox: (id: number, data: Partial<Mailbox>) =>
    apiClient.patch<Mailbox>(`/helpdesk/settings/mailboxes/${id}/`, data),
  deleteMailbox: (id: number) => apiClient.delete(`/helpdesk/settings/mailboxes/${id}/`),
  testMailbox: (id: number, testType: 'imap' | 'smtp' | 'both' = 'both') =>
    apiClient.post<EmailTestResponse>(`/helpdesk/settings/mailboxes/${id}/test/`, {
      test_type: testType,
    }),
  /** Реально отправить тестовое письмо с ящика на указанный адрес */
  sendTestMail: (id: number, to: string) =>
    apiClient.post<EmailTestResponse>(`/helpdesk/settings/mailboxes/${id}/test/`, {
      test_type: 'send',
      to,
    }),
  pollMailbox: (id: number) =>
    apiClient.post(`/helpdesk/settings/mailboxes/${id}/poll/`),
};
